import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const ContactForm = () => {
    const { t } = useTranslation();
    const [formValues, setFormValues] = useState({
        name: '',
        email: '',
        phone: '',
        service: '',
        note: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({
            ...formValues,
            [name]: value
        });
    };

    const validate = (values) => {
        let errors = {};
        if (!values.name) {
            errors.name = t('form.errors.name');
        }
        if (!values.email) {
            errors.email = t('form.errors.email');
        } else if (!/\S+@\S+\.\S+/.test(values.email)) {
            errors.email = t('form.errors.email_invalid');
        }
        if (!values.phone) {
            errors.phone = t('form.errors.phone');
        }
        if (!values.service) {
            errors.service = t('form.errors.service');
        }
        if (!values.note) {
            errors.note = t('form.errors.note');
        }
        return errors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormErrors(validate(formValues));
        setIsSubmitting(true);
    };

    const handleFormSubmission = async () => {
        setIsLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formValues.name,
                    email: formValues.email,
                    phone: formValues.phone,
                    message: `Service: ${formValues.service}, Note: ${formValues.note}`
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSuccessMessage(t('form.success'));
                setFormValues({
                    name: '',
                    email: '',
                    phone: '',
                    service: '',
                    note: ''
                });
            } else {
                throw new Error(data.error || t('form.error'));
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setErrorMessage(t('form.error'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (Object.keys(formErrors).length === 0 && isSubmitting) {
            handleFormSubmission();
            setIsSubmitting(false);
        }
    }, [formErrors, isSubmitting, handleFormSubmission]);

    return (
        <form onSubmit={handleSubmit} className="office-info-text" id="contact-form-main">
            <div className="row">
                <div className="col col-lg-6 col-md-6 col-12">
                    <input
                        type="text"
                        className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                        name="name"
                        id="name"
                        placeholder={t('Form.name')}
                        value={formValues.name}
                        onChange={handleChange}
                        required
                    />
                    {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                </div>
                <div className="col col-lg-6 col-md-6 col-12">
                    <input
                        type="email"
                        className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                        name="email"
                        id="email"
                        placeholder={t('Form.email')}
                        value={formValues.email}
                        onChange={handleChange}
                        required
                    />
                    {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                </div>
                <div className="col col-lg-6 col-md-6 col-12">
                    <input
                        type="tel"
                        className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
                        name="phone"
                        id="phone"
                        placeholder={t('Form.phone')}
                        value={formValues.phone}
                        onChange={handleChange}
                        required
                    />
                    {formErrors.phone && <div className="invalid-feedback">{formErrors.phone}</div>}
                </div>
                <div className="col col-lg-6 col-md-6 col-12">
                    <select
                        name="service"
                        className={`form-control ${formErrors.service ? 'is-invalid' : ''} ${formValues.service ? 'text-black' : 'text-gray-400'}`}
                        value={formValues.service}
                        onChange={handleChange}
                        required
                    >
                        <option disabled value="" style={{ color: '#999' }}>
                            {t('Form.service')}
                        </option>
                        <option style={{ color: 'black' }}>{t('consulting_services')}</option>
                        <option style={{ color: 'black' }}>{t('international_trading')}</option>
                        <option style={{ color: 'black' }}>{t('transportation_logistics')}</option>
                    </select>
                    {formErrors.service && <div className="invalid-feedback">{formErrors.service}</div>}
                </div>
                <div className="fullwidth col col-lg-12 col-12">
                    <textarea
                        className={`form-control ${formErrors.note ? 'is-invalid' : ''}`}
                        name="note"
                        id="note"
                        placeholder={t('Form.note')}
                        value={formValues.note}
                        onChange={handleChange}
                        required
                    />
                    {formErrors.note && <div className="invalid-feedback">{formErrors.note}</div>}
                </div>
            </div>
            <div className="submit-area">
                <button type="submit" className="theme-btn-s4" disabled={isLoading}>
                    {isLoading ? <i className="ti-reload"></i> : t('Form.submit')}
                </button>
            </div>
            <div className="clearfix error-handling-messages">
                {successMessage && <div id="success" className="text-success">{successMessage}</div>}
                {errorMessage && <div id="error" className="text-danger">{errorMessage}</div>}
            </div>
        </form>
    );
};

export default ContactForm;